import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, getThumbUrl } from '../../shared/api';

const NewRoomModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const [roomType, setRoomType] = useState('DIRECT');
  const [groupName, setGroupName] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Manage body scroll-lock and clean up any Bootstrap-injected backdrop on unmount.
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    };
  }, []);

  const { data: usersData, isFetching: loading } = useQuery({
    queryKey: ['chat', 'users', debouncedSearch],
    queryFn: () => api.get(`chat/users/?search=${encodeURIComponent(debouncedSearch)}`),
    select: (data) => data.results ?? data,
    staleTime: 30_000,
  });
  const users = usersData ?? [];

  const toggleUser = (id) => {
    if (roomType === 'DIRECT') {
      setSelectedIds([id]);
    } else {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      );
    }
  };

  const createRoomMutation = useMutation({
    mutationFn: () => api.post('chat/rooms/', {
      type: roomType,
      name: groupName.trim(),
      member_ids: selectedIds,
    }),
    onSuccess: (room) => onCreated(room),
    onError: (e) => setError(e.message || t('common.error')),
  });

  const handleSubmit = () => {
    if (selectedIds.length === 0) {
      setError(t('chat.selectAtLeastOne'));
      return;
    }
    if (roomType === 'GROUP' && !groupName.trim()) {
      setError(t('chat.groupNameRequired'));
      return;
    }
    setError('');
    createRoomMutation.mutate();
  };

  const submitting = createRoomMutation.isPending;

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div className="modal modal-blur fade show d-block" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t('chat.newRoom')}</h5>
            <button className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <div className="modal-body">
            {/* Type toggle */}
            <div className="mb-3">
              <div className="btn-group w-100">
                <button
                  className={`btn btn-sm ${roomType === 'DIRECT' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => { setRoomType('DIRECT'); setSelectedIds([]); }}
                >
                  {t('chat.directMessage')}
                </button>
                <button
                  className={`btn btn-sm ${roomType === 'GROUP' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => { setRoomType('GROUP'); setSelectedIds([]); }}
                >
                  {t('chat.groupChat')}
                </button>
              </div>
            </div>

            {/* Group name */}
            {roomType === 'GROUP' && (
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t('chat.groupName')}
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                />
              </div>
            )}

            {/* Search */}
            <div className="mb-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={t('chat.searchUsers')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            {/* User list */}
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-2">
                  <span className="spinner-border spinner-border-sm text-secondary" />
                </div>
              ) : users.length === 0 ? (
                <p className="text-muted text-center py-2 small">{t('chat.noUsersFound')}</p>
              ) : (
                users.map((u) => {
                  const selected = selectedIds.includes(u.id);
                  return (
                    <div
                      key={u.id}
                      className={`d-flex align-items-center gap-2 py-1 px-2 rounded cursor-pointer ${selected ? 'bg-primary-lt' : 'hover-bg'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleUser(u.id)}
                    >
                      <div
                        className="avatar avatar-xs"
                        style={{ background: 'var(--tblr-secondary-lt)', color: 'var(--tblr-secondary)', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}
                      >
                        {u.avatar ? (
                          <img src={getThumbUrl(u.avatar, 200)} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          (u.full_name || u.username || '?')[0].toUpperCase()
                        )}
                      </div>
                      <span className="flex-grow-1 small">{u.full_name || u.username}</span>
                      {selected && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                          stroke="var(--tblr-primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <path d="M5 12l5 5l10 -10" />
                        </svg>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {error && <p className="text-danger small mt-2">{error}</p>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost-secondary me-auto" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting || selectedIds.length === 0}
            >
              {submitting ? (
                <span className="spinner-border spinner-border-sm me-1" />
              ) : null}
              {t('chat.create')}
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default NewRoomModal;
